import { useEffect, useMemo, useState } from 'react'
import { TrendingUp, TrendingDown, Sparkles, X, MessageCircle, Zap, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { API_BASE } from '@/api/chat'
import { PageHeader } from '../shell/PageHeader'
import { AISparkle } from '@/components/ui/AISparkle'
import { Button } from '@/components/ui/Button'
import { HealthRing } from '@/components/HealthRing'
import { GamificationBar } from '@/components/GamificationBar'
import { LeaderboardPage } from './LeaderboardPage'
import { RewardsPage } from './RewardsPage'
import { healthScore } from '../data/score'
import {
  PATIENT, CONDITIONS, MEDICATIONS, LABS, TRENDS, DAY_LABELS, PATTERNS, CONNECTED_SOURCES,
  type Trend,
} from '../data/record'
import { buildLiveTrends, sourceLabel, type LiveMetric, type LiveTrend } from '../data/liveTrends'

type Lab = (typeof LABS)[number]
type Detail = { kind: 'trend'; trend: Trend; labels?: string[] } | { kind: 'lab'; lab: Lab } | null

// Show the "syncing" skeleton only on the first visit this session.
let recordSeen = false

export function RecordPage({
  onMenu, showMenu, onAskSara, right, userName, userId = 'demo-user',
}: {
  onMenu: () => void
  showMenu: boolean
  onAskSara: (prompt: string) => void
  right?: React.ReactNode
  userName?: string
  userId?: string
}) {
  const displayName = userName?.trim() || PATIENT.name
  const [detail, setDetail] = useState<Detail>(null)
  const [loading, setLoading] = useState(!recordSeen)
  const [rawMetrics, setRawMetrics] = useState<LiveMetric[]>([])
  const [leaderboardOpen, setLeaderboardOpen] = useState(false)
  const [rewardsOpen, setRewardsOpen] = useState(false)
  const hs = healthScore()

  useEffect(() => {
    if (!loading) return
    const t = setTimeout(() => { recordSeen = true; setLoading(false) }, 750)
    return () => clearTimeout(t)
  }, [loading])

  // Live, encrypted readings actually synced from the user's connected devices (WHOOP, Withings,
  // Google Health, Apple Health…). Full history, so multi-day metrics become real trend charts.
  useEffect(() => {
    fetch(`${API_BASE}/v1/health/metrics/${encodeURIComponent(userId)}?limit=500`)
      .then((r) => (r.ok ? r.json() : { metrics: [] }))
      .then((d: { metrics: LiveMetric[] }) => setRawMetrics(d.metrics || []))
      .catch(() => {})
  }, [userId])

  // Multi-day metrics (≥2 distinct days) become real trend charts; everything else stays a
  // plain snapshot tile until enough history accumulates.
  const liveTrends = useMemo(() => buildLiveTrends(rawMetrics), [rawMetrics])
  const chartedTypes = useMemo(() => new Set(liveTrends.map((t) => t.type)), [liveTrends])
  const snapshots = useMemo(() => {
    const latest = new Map<string, LiveMetric>()
    for (const m of rawMetrics) if (!chartedTypes.has(m.type)) latest.set(m.type, m) // oldest→newest
    return [...latest.values()]
  }, [rawMetrics, chartedTypes])

  if (loading) return <RecordSkeleton onMenu={onMenu} showMenu={showMenu} />

  return (
    <div className="flex h-full flex-1 min-w-0 flex-col bg-background">
      <PageHeader
        title="My health record"
        subtitle={`One record · synced from ${CONNECTED_SOURCES.length} sources`}
        onMenu={onMenu}
        showMenu={showMenu}
        right={right}
      />
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-5xl px-4 md:px-6 py-5 space-y-4">
          {/* Streak + Health Coins */}
          <GamificationBar
            userId={userId}
            onOpenLeaderboard={() => setLeaderboardOpen(true)}
            onOpenRewards={() => setRewardsOpen(true)}
          />

          {/* Identity strip */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-border bg-card px-4 py-3">
            <div className="flex size-9 items-center justify-center rounded-full bg-accent text-accent-foreground text-sm font-semibold">{displayName[0]}</div>
            <div className="text-base font-semibold text-foreground">{displayName}</div>
            <Meta label="Age" value={`${PATIENT.age} · ${PATIENT.sex}`} />
            <Meta label="MRN" value={PATIENT.mrn} />
            <Meta label="PCP" value={PATIENT.pcp} />
            <div className="ml-auto hidden md:flex flex-wrap items-center gap-1.5">
              {CONNECTED_SOURCES.map((s) => (
                <span key={s} className="rounded-full border border-border-subtle px-2 py-0.5 text-[11px] text-muted-foreground">{s}</span>
              ))}
            </div>
          </div>

          {/* Hero — composite health score (centered ring, WHOOP-style) */}
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-card px-5 py-6 text-center animate-scale-in">
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Your health score today</div>
            <HealthRing score={hs.score} tone={hs.tone} label={hs.label} size={148} />
            <h2 className="text-xl font-semibold text-foreground">
              {hs.score >= 80 ? "You're trending strong." : hs.score >= 60 ? 'A few things need your attention.' : "Let's turn this around together."}
            </h2>
            <p className="text-sm text-muted-foreground">
              Your blood pressure and sleep are the biggest levers right now — and they're connected.
            </p>
            <Button variant="primary" size="sm" className="mx-auto mt-1 gap-2"
              onClick={() => onAskSara('Looking at my whole record, what are the top 2 things I should focus on this week, and why?')}>
              <MessageCircle className="size-4" /> Ask SARA what to focus on
            </Button>
          </div>

          {/* Live readings — real, encrypted data synced from connected devices */}
          {(liveTrends.length > 0 || snapshots.length > 0) && (
            <section>
              <div className="mb-2 flex items-center gap-2">
                <Zap className="size-4 text-info-text" />
                <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Live from your devices</h2>
                <span className="inline-flex items-center gap-1 rounded-full bg-success-light px-1.5 py-0.5 text-[10px] font-medium text-success-text">
                  <RefreshCw className="size-2.5" /> synced
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {liveTrends.map((t) => (
                  <button
                    key={t.type}
                    onClick={() => setDetail({ kind: 'trend', trend: t, labels: t.labels })}
                    className="lift group text-left rounded-xl border border-info-text/30 bg-card p-3 hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] text-muted-foreground truncate">{t.metric}</span>
                      {t.dir === 'up'
                        ? <TrendingUp className={cn('size-3', t.concern ? 'text-alert-text' : 'text-muted-foreground')} />
                        : <TrendingDown className={cn('size-3', t.concern ? 'text-alert-text' : 'text-muted-foreground')} />}
                    </div>
                    <div className="mt-0.5 text-lg font-semibold text-foreground leading-none">
                      {t.series[t.series.length - 1].toLocaleString()}
                      <span className="text-[11px] font-normal text-muted-foreground"> {t.unit}</span>
                    </div>
                    <div className="mt-2"><Sparkline values={t.series} concern={t.concern} /></div>
                    <div className="mt-1 text-[10px] text-muted-foreground">{t.source} · {t.series.length}d</div>
                  </button>
                ))}
                {snapshots.map((m) => (
                  <div key={m.type} className="rounded-xl border border-info-text/30 bg-card p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] text-muted-foreground truncate">{metricLabel(m.type)}</span>
                      <span className="text-[9px] text-muted-foreground">{sourceLabel(m.source)}</span>
                    </div>
                    <div className="mt-0.5 text-lg font-semibold text-foreground leading-none">
                      {m.value.toLocaleString()}
                      <span className="text-[11px] font-normal text-muted-foreground"> {m.unit}</span>
                    </div>
                    <div className="mt-1.5 text-[10px] text-muted-foreground">{relTime(m.recorded_at)}</div>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">Encrypted at rest · SARA uses these in your coaching.</p>
            </section>
          )}

          {/* Vitals — clickable tiles */}
          <Section title="Vitals at a glance · tap for detail">
            <div className="grid grid-cols-2 gap-3">
              {TRENDS.map((t) => (
                <button
                  key={t.metric}
                  onClick={() => setDetail({ kind: 'trend', trend: t })}
                  className="lift group text-left rounded-xl border border-border bg-card p-3 hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground truncate">{t.metric}</span>
                    {t.dir === 'up'
                      ? <TrendingUp className={cn('size-3', t.concern ? 'text-alert-text' : 'text-muted-foreground')} />
                      : <TrendingDown className={cn('size-3', t.concern ? 'text-alert-text' : 'text-muted-foreground')} />}
                  </div>
                  <div className="mt-0.5 text-lg font-semibold text-foreground leading-none">
                    {t.series[t.series.length - 1].toLocaleString()}
                    <span className="text-[11px] font-normal text-muted-foreground"> {t.unit}</span>
                  </div>
                  <div className="mt-2"><Sparkline values={t.series} concern={t.concern} /></div>
                  <div className="mt-1 text-[10px] text-muted-foreground">{t.source}</div>
                </button>
              ))}
            </div>
          </Section>

          <div className="flex flex-col gap-4">
            <div className="lg:col-span-2 space-y-4">
              <Section title="Recent labs & vitals · tap for detail">
                <div className="grid grid-cols-1 gap-2">
                  {LABS.map((l) => (
                    <button
                      key={l.name}
                      onClick={() => setDetail({ kind: 'lab', lab: l })}
                      className="lift flex items-center justify-between gap-2 rounded-lg border border-border-subtle bg-card px-3 py-2 text-left hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-foreground truncate">{l.name}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{l.source} · {l.date}</div>
                      </div>
                      <span className={cn('shrink-0 rounded-md px-2 py-0.5 text-xs font-medium',
                        l.flag === 'borderline' ? 'bg-warning-light text-warning-text' : 'bg-success-light text-success-text')}>{l.value}</span>
                    </button>
                  ))}
                </div>
              </Section>

              <Section title="Conditions & medications">
                <div className="grid grid-cols-1 gap-2">
                  {CONDITIONS.map((c) => <Pill key={c.name} title={c.name} sub={`since ${c.since}`} tag={c.source} />)}
                  {MEDICATIONS.map((m) => <Pill key={m.name} title={m.name} sub={m.schedule} tag={m.source} />)}
                </div>
              </Section>
            </div>

            {/* Patterns — clickable → ask SARA */}
            <div className="rounded-xl border border-border bg-ornament-plum-ultra-subtle p-4">
              <div className="mb-3 flex items-center gap-2">
                <AISparkle size="sm" />
                <h2 className="text-sm font-semibold text-foreground">Patterns SARA has connected</h2>
              </div>
              <ul className="space-y-2">
                {PATTERNS.map((p) => (
                  <li key={p}>
                    <button
                      onClick={() => onAskSara(`Explain this pattern you noticed in my data: "${p}"`)}
                      className="flex w-full gap-2.5 rounded-lg p-1.5 text-left text-sm leading-snug text-foreground transition-colors hover:bg-card focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <Sparkles className="mt-0.5 size-4 shrink-0 text-ornament-plum" />
                      {p}
                    </button>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-[11px] text-muted-foreground">Tap a pattern to ask SARA about it.</p>
            </div>
          </div>
        </div>
      </div>

      {detail && <DetailModal detail={detail} onClose={() => setDetail(null)} onAskSara={onAskSara} />}
      {leaderboardOpen && <LeaderboardPage userId={userId} onClose={() => setLeaderboardOpen(false)} />}
      {rewardsOpen && <RewardsPage userId={userId} onClose={() => setRewardsOpen(false)} />}
    </div>
  )
}

function DetailModal({ detail, onClose, onAskSara }: { detail: NonNullable<Detail>; onClose: () => void; onAskSara: (p: string) => void }) {
  const isTrend = detail.kind === 'trend'
  const title = isTrend ? detail.trend.metric : detail.lab.name
  const source = isTrend ? detail.trend.source : detail.lab.source
  const ask = isTrend
    ? `Walk me through my ${detail.trend.metric.toLowerCase()} trend this week — what's driving it and what should I do?`
    : `What does my ${detail.lab.name} of ${detail.lab.value} mean for me, and should I act on it?`

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-overlay p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-card p-5 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold text-foreground">{title}</h2>
            <p className="text-[11px] text-muted-foreground">Source: {source}</p>
          </div>
          <button onClick={onClose} className="p-1.5 -mr-1.5 rounded-md hover:bg-muted" aria-label="Close"><X className="size-5 text-foreground" /></button>
        </div>

        {isTrend ? <TrendBody t={detail.trend} labels={detail.labels} /> : <LabBody l={detail.lab} />}

        <Button variant="primary" className="mt-4 w-full gap-2" onClick={() => onAskSara(ask)}>
          <MessageCircle className="size-4" /> Ask SARA about this
        </Button>
      </div>
    </div>
  )
}

function TrendBody({ t, labels }: { t: Trend; labels?: string[] }) {
  const latest = t.series[t.series.length - 1]
  const avg = Math.round((t.series.reduce((a, b) => a + b, 0) / t.series.length) * 10) / 10
  const min = Math.min(...t.series), max = Math.max(...t.series)
  return (
    <div className="mt-3">
      <BigChart t={t} labels={labels} />
      <div className="mt-3 grid grid-cols-3 gap-2">
        <Stat label="Latest" value={`${latest.toLocaleString()} ${t.unit}`} />
        <Stat label={`${t.series.length}-day avg`} value={`${avg.toLocaleString()} ${t.unit}`} />
        <Stat label="Range" value={`${min.toLocaleString()}–${max.toLocaleString()}`} />
      </div>
      <div className="mt-3 rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Normal: </span>{t.ref}
      </div>
      <p className="mt-2 text-sm text-foreground">{t.note}</p>
    </div>
  )
}

function LabBody({ l }: { l: Lab }) {
  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-baseline gap-3">
        <span className={cn('rounded-md px-2.5 py-1 text-lg font-semibold',
          l.flag === 'borderline' ? 'bg-warning-light text-warning-text' : 'bg-success-light text-success-text')}>{l.value}</span>
        <span className="text-sm text-muted-foreground">measured {l.date}</span>
      </div>
      <div className="rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Reference range: </span>{l.ref}
      </div>
      <p className="text-sm text-foreground">
        {l.flag === 'borderline'
          ? 'This reading is just outside the normal range — worth watching and addressing with lifestyle changes. Not an emergency.'
          : 'This reading is within the normal range.'}
      </p>
    </div>
  )
}

function BigChart({ t, labels = DAY_LABELS }: { t: Trend; labels?: string[] }) {
  const W = 460, H = 150, padX = 28, padTop = 26, padBot = 22
  const vals = t.series
  const lo = Math.min(...vals, t.normal), hi = Math.max(...vals, t.normal)
  const range = hi - lo || 1
  const x = (i: number) => padX + (i / (vals.length - 1)) * (W - padX * 2)
  const y = (v: number) => padTop + (1 - (v - lo) / range) * (H - padTop - padBot)
  const line = vals.map((v, i) => `${x(i)},${y(v)}`).join(' ')
  const baseY = H - padBot
  const stroke = t.concern ? 'var(--alert)' : 'var(--success)'
  const gid = `big-${t.concern ? 'a' : 'b'}`
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label={`${t.metric} 7-day chart`}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={stroke} stopOpacity="0.20" />
          <stop offset="1" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`${padX},${baseY} ${line} ${W - padX},${baseY}`} fill={`url(#${gid})`} />
      {/* reference (normal) line */}
      <line x1={padX} x2={W - padX} y1={y(t.normal)} y2={y(t.normal)} stroke="var(--border)" strokeWidth="1" strokeDasharray="4 4" />
      <text x={W - padX} y={y(t.normal) - 4} textAnchor="end" className="fill-[var(--muted-foreground)]" fontSize="9">normal</text>
      {/* trend line */}
      <polyline points={line} fill="none" stroke={stroke} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {vals.map((v, i) => (
        <g key={i}>
          <circle cx={x(i)} cy={y(v)} r="3" fill={stroke} />
          <text x={x(i)} y={y(v) - 8} textAnchor="middle" className="fill-[var(--foreground)]" fontSize="9" fontWeight="600">{v.toLocaleString()}</text>
          <text x={x(i)} y={H - 6} textAnchor="middle" className="fill-[var(--muted-foreground)]" fontSize="9">{labels[i]}</text>
        </g>
      ))}
    </svg>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border-subtle bg-background px-3 py-2 text-center">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold text-foreground">{value}</div>
    </div>
  )
}

function RecordSkeleton({ onMenu, showMenu }: { onMenu: () => void; showMenu: boolean }) {
  return (
    <div className="flex h-full flex-1 min-w-0 flex-col bg-background">
      <PageHeader title="My health record" subtitle="Syncing from your connected sources…" onMenu={onMenu} showMenu={showMenu} />
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-5xl px-4 md:px-6 py-5 space-y-4">
          <div className="h-14 rounded-xl skeleton" />
          <div className="h-28 rounded-2xl skeleton" />
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-24 rounded-xl skeleton" />)}
          </div>
          <div className="flex flex-col gap-4">
            <div className="lg:col-span-2 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-12 rounded-lg skeleton" />)}
            </div>
            <div className="h-72 rounded-xl skeleton" />
          </div>
        </div>
      </div>
    </div>
  )
}

const METRIC_LABELS: Record<string, string> = {
  resting_heart_rate: 'Resting HR', heart_rate: 'Heart rate', hrv: 'HRV', recovery_score: 'Recovery',
  spo2: 'SpO₂', strain: 'Strain', sleep_hours: 'Sleep', sleep_performance: 'Sleep performance',
  steps: 'Steps', weight: 'Weight', systolic_bp: 'Systolic BP', diastolic_bp: 'Diastolic BP',
  blood_glucose: 'Glucose',
}
function metricLabel(t: string): string {
  return METRIC_LABELS[t] || t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}
function relTime(iso: string): string {
  const t = Date.parse(iso)
  if (!iso || Number.isNaN(t)) return 'recently'
  const mins = Math.round((Date.now() - t) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.round(hrs / 24)}d ago`
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
      {children}
    </section>
  )
}
function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm text-foreground">{value}</div>
    </div>
  )
}
function Pill({ title, sub, tag }: { title: string; sub: string; tag: string }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-border-subtle bg-card px-3 py-2">
      <div className="min-w-0">
        <div className="text-sm text-foreground truncate">{title}</div>
        <div className="text-[11px] text-muted-foreground truncate">{sub}</div>
      </div>
      <span className="shrink-0 text-[10px] text-muted-foreground">{tag}</span>
    </div>
  )
}
function Sparkline({ values, concern }: { values: number[]; concern: boolean }) {
  const w = 100, h = 24, min = Math.min(...values), max = Math.max(...values)
  const range = max - min || 1
  const line = values.map((v, i) => `${(i / (values.length - 1)) * w},${h - ((v - min) / range) * h}`).join(' ')
  const stroke = concern ? 'var(--alert)' : 'var(--success)'
  const gid = concern ? 'spk-a' : 'spk-b'
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="block">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={stroke} stopOpacity="0.24" />
          <stop offset="1" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${line} ${w},${h}`} fill={`url(#${gid})`} />
      <polyline points={line} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}
